import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { Loader2, Zap, CheckCircle2, XCircle, AlertCircle, StopCircle, RotateCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { fmtRelative } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";
import { pollWhenVisible } from "@/lib/pollWhenVisible";

/** Iter 175 · Global Jobs popover — surfaces in-flight bg_jobs in the header
 *  so users can peek at bulk AI briefs / competitor analysis progress without
 *  navigating away. Polls every 20s.
 *
 *  Iter 228 · Added Cancel (running) + Retry (failed/partial) actions plus
 *  a "stuck" visual once a running job has been going > 15 min. Fixes the
 *  case where a uvicorn restart leaves rows in `running` forever. */

const JOB_LABELS = {
  brand_brief_bulk: "Bulk brand brief",
  competitor_bulk_ai: "Competitor analysis",
};

const STATUS_ICON = {
  running: <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-500" />,
  pending: <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />,
  done: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />,
  partial: <AlertCircle className="w-3.5 h-3.5 text-amber-500" />,
  failed: <XCircle className="w-3.5 h-3.5 text-rose-500" />,
};

// A job that has been "running" for longer than this is almost certainly
// stuck (uvicorn died mid-run). We surface a warning + Cancel CTA.
const STUCK_MS = 15 * 60 * 1000;

function JobRow({ j, wsId, onChange }) {
  const label = JOB_LABELS[j.kind] || j.kind;
  const target = j.meta?.brand_name || (j.meta?.brand_count && `${j.meta.brand_count} brands`) || "—";
  const pct = j.total > 0 ? Math.round((j.done / j.total) * 100) : 0;
  const running = j.status === "running" || j.status === "pending";
  const retryable = j.status === "failed" || j.status === "partial";
  const startedAt = j.started_at || j.created_at;
  const stuck = running && startedAt && (Date.now() - new Date(startedAt).getTime()) > STUCK_MS;
  const [busy, setBusy] = useState(null);   // "cancel" | "retry" | null

  const cancel = async () => {
    setBusy("cancel");
    try {
      await api.post(`/workspaces/${wsId}/jobs/${j.id}/cancel`);
      toast.success("Job cancelled");
      onChange?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Cancel failed"));
    } finally { setBusy(null); }
  };

  const retry = async () => {
    setBusy("retry");
    try {
      await api.post(`/workspaces/${wsId}/jobs/${j.id}/retry`);
      toast.success("Job re-queued");
      onChange?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Retry failed"));
    } finally { setBusy(null); }
  };

  return (
    <div className={`p-2.5 border-b border-border last:border-b-0 ${stuck ? "bg-amber-50/60" : ""}`} data-testid={`jobs-row-${j.id}`}>
      <div className="flex items-center gap-2">
        {STATUS_ICON[j.status] || <Loader2 className="w-3.5 h-3.5" />}
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium truncate flex items-center gap-1.5">
            {label}
            {stuck && (
              <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase text-amber-700 border border-amber-400 bg-amber-100 px-1"
                    data-testid={`jobs-row-${j.id}-stuck`} title="Running longer than 15 minutes — likely stuck">
                <AlertTriangle className="w-2.5 h-2.5" /> Stuck
              </span>
            )}
          </div>
          <div className="text-[10px] text-muted-foreground truncate">
            {target} · {fmtRelative(j.created_at)}
          </div>
        </div>
        <span className="text-[10px] font-mono text-muted-foreground shrink-0">
          {j.done}/{j.total}
          {j.failed ? ` · ${j.failed}✖` : ""}
        </span>
      </div>
      {running && (
        <div className="mt-1 h-1 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-sky-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}
      {(running || retryable) && (
        <div className="mt-2 flex items-center gap-1.5">
          {running && (
            <button
              type="button"
              onClick={cancel}
              disabled={busy === "cancel"}
              className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 border border-border hover:border-rose-400 hover:text-rose-600 transition disabled:opacity-50"
              data-testid={`jobs-row-${j.id}-cancel`}
            >
              {busy === "cancel" ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <StopCircle className="w-2.5 h-2.5" />}
              Cancel
            </button>
          )}
          {retryable && (
            <button
              type="button"
              onClick={retry}
              disabled={busy === "retry"}
              className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 border border-border hover:border-primary hover:text-primary transition disabled:opacity-50"
              data-testid={`jobs-row-${j.id}-retry`}
            >
              {busy === "retry" ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <RotateCcw className="w-2.5 h-2.5" />}
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function JobsIndicator() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!wsId) { setJobs([]); return; }
    try {
      const r = await api.get(`/workspaces/${wsId}/jobs`, { params: { limit: 12 } });
      setJobs(r.data.rows || []);
    } catch { setJobs([]); }
  }, [wsId]);

  useEffect(() => {
    if (!wsId) return;
    let cancelled = false;
    const tick = async () => { if (!cancelled) await load(); };
    tick();
    // Feb 2026 R281 · Bumped 20s → 60s + visibility-gated. Bulk AI jobs
    // don't need sub-minute freshness in the header; the drop-down
    // itself calls load() when opened.
    const stopPoll = pollWhenVisible(tick, 60_000);
    return () => { cancelled = true; stopPoll(); };
  }, [wsId, load]);

  const inflight = jobs.filter((j) => j.status === "running" || j.status === "pending");
  const stuckCount = inflight.filter((j) => {
    const s = j.started_at || j.created_at;
    return s && (Date.now() - new Date(s).getTime()) > STUCK_MS;
  }).length;

  if (jobs.length === 0) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger className="relative h-8 px-2 flex items-center gap-1.5 hover:bg-muted transition-colors border border-border rounded-none"
                                title={inflight.length > 0 ? `${inflight.length} job${inflight.length === 1 ? "" : "s"} running${stuckCount ? ` · ${stuckCount} stuck` : ""}` : "Recent AI jobs"}
                                data-testid="jobs-indicator-trigger">
        {inflight.length > 0
          ? <Loader2 className={`w-3.5 h-3.5 animate-spin ${stuckCount ? "text-amber-500" : "text-sky-500"}`} />
          : <Zap className="w-3.5 h-3.5 text-muted-foreground" />}
        <span className="text-xs font-medium hidden md:inline">
          {inflight.length > 0 ? `${inflight.length} running` : "Jobs"}
        </span>
        {inflight.length > 0 && (
          <span className={`absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full text-white text-[9px] font-bold flex items-center justify-center ${stuckCount ? "bg-amber-500" : "bg-sky-500"}`}
                  data-testid="jobs-indicator-badge">
            {inflight.length}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0 rounded-none" data-testid="jobs-indicator-menu">
        <div className="px-3 py-2 border-b border-border bg-muted/30">
          <div className="text-xs font-semibold flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5" /> Background jobs
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            {inflight.length} running · {jobs.filter((j) => j.status === "done").length} done
            {stuckCount ? ` · ${stuckCount} stuck` : ""} · Auto-refresh 60s
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto" data-testid="jobs-indicator-list">
          {jobs.map((j) => <JobRow key={j.id} j={j} wsId={wsId} onChange={load} />)}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
